import LegalPageLayout from "@/components/LegalPageLayout";

const PrivacyPolicyPage = () => (
  <LegalPageLayout title="Privacy Policy" lastUpdated="April 2026" testId="privacy-policy-page">
    <p>
      At <strong>Rev &amp; Dev Children Clinic</strong> (operating the <strong>tots360</strong> platform) we take your
      privacy — and especially the privacy of children whose data you share with us — very seriously. This Privacy Policy
      describes how we collect, use, store, share and protect your personal information when you use our Platform.
    </p>

    <h2>1. Who we are</h2>
    <p>
      <strong>Data Controller:</strong> Rev &amp; Dev Children Clinic, a medical establishment registered in India.
      <br />
      <strong>Address:</strong> 2nd Floor, Fairmount Fortune One, Czech Colony, Sanath Nagar, Hyderabad — 500018, Telangana, India.
      <br />
      <strong>Contact:</strong> <a href="mailto:info@tots360.in">info@tots360.in</a> · +91 89857 09090
    </p>

    <h2>2. Information we collect</h2>
    <h3>a. Information you provide</h3>
    <ul>
      <li>Account details — name, email address, phone number, role (parent / doctor / school admin / class teacher), country, timezone and password hash.</li>
      <li>Child health inputs — child name, date of birth / age, sex, height, weight, bone-age X-ray images (for BoneAH), parental heights, diet preference, region.</li>
      <li>School information (for school admins) — school name, address, board, logo file, contact person.</li>
      <li>Payment information — transaction amount, currency, coupon code, payment status, gateway order and payment IDs. <strong>We do not store your card or UPI details</strong>; these are handled directly by Razorpay or Stripe.</li>
      <li>Communications — any message, feedback or support request you send us.</li>
    </ul>
    <h3>b. Information we collect automatically</h3>
    <ul>
      <li>Usage data — pages visited, session duration, device type, browser, IP address (truncated where possible).</li>
      <li>Cookies and similar technologies — used for authentication (session tokens) and for anonymised analytics via Google Analytics 4. Tracking is <strong>disabled on medical-tool pages</strong> and authentication pages to protect sensitive data.</li>
    </ul>

    <h2>3. How we use your information</h2>
    <ul>
      <li>To create and manage your account and authenticate you.</li>
      <li>To generate, store and re-deliver your AI-assisted reports.</li>
      <li>To process payments and maintain records for tax and accounting.</li>
      <li>To respond to enquiries, provide customer support and send service-related emails (e.g., verification, password reset, purchase receipt).</li>
      <li>To monitor and improve Platform performance, fix bugs and enhance the Service.</li>
      <li>To meet legal, regulatory, or audit obligations.</li>
    </ul>

    <h2>4. Legal bases for processing (where applicable)</h2>
    <ul>
      <li><strong>Consent</strong> — provided during signup and when you submit a child&rsquo;s health data.</li>
      <li><strong>Performance of contract</strong> — to deliver the Service you requested.</li>
      <li><strong>Legitimate interests</strong> — security, fraud prevention, and improving our product.</li>
      <li><strong>Legal obligation</strong> — tax, accounting and regulatory requirements under Indian law.</li>
    </ul>

    <h2>5. Children&rsquo;s data — special safeguards</h2>
    <p>
      Child health data is submitted <strong>by a parent, legal guardian or authorised healthcare professional</strong>.
      By uploading such data, you represent that you have the legal authority to do so. We do not knowingly allow children under
      18 to create their own accounts or submit their own data. We retain child-related inputs only as long as necessary to
      generate and store the associated report and comply with our record-keeping obligations.
    </p>

    <h2>6. How we share information</h2>
    <p>We do <strong>not</strong> sell your personal information. We share information only as follows:</p>
    <ul>
      <li><strong>With payment processors</strong> — Razorpay (India) and Stripe (international) — to complete transactions. These processors are PCI-DSS certified and independently regulated.</li>
      <li><strong>With service providers</strong> acting on our behalf — cloud hosting (Emergent infrastructure), email delivery (Resend), authentication (Emergent-managed Google OAuth), object storage for school logos — under confidentiality obligations.</li>
      <li><strong>Within your organisation</strong> — if you access the Platform via a school account, aggregated (non-individual) statistics may be visible to the school administrator.</li>
      <li><strong>To comply with law</strong> — in response to a valid legal request (court order, regulatory directive) or to protect our rights and the safety of users.</li>
    </ul>

    <h2>7. Security</h2>
    <ul>
      <li>All traffic is encrypted in transit using TLS 1.2 or higher.</li>
      <li>Passwords are hashed with industry-standard bcrypt.</li>
      <li>Sessions use HTTP-only, secure, same-site cookies (and a Bearer token fallback) that expire after 7 days.</li>
      <li>Brute-force protection, IP-based audit logging, and role-based access control (RBAC) are enforced throughout the backend.</li>
      <li>Despite our efforts, no system is 100% secure. If you suspect a compromise, please contact us immediately at <a href="mailto:info@tots360.in">info@tots360.in</a>.</li>
    </ul>

    <h2>8. Data retention</h2>
    <p>
      We retain your personal data only for as long as necessary to deliver the Service and to meet legal and accounting
      obligations. Typical retention periods:
    </p>
    <ul>
      <li>Account + health records: duration of account + 7 years thereafter (for medical and tax record-keeping).</li>
      <li>Payment transaction records: 8 years (as required under Indian tax law).</li>
      <li>Session tokens: auto-expire after 7 days.</li>
      <li>Analytics data: aggregated and anonymised after 14 months.</li>
    </ul>

    <h2>9. Your rights</h2>
    <p>Subject to applicable law (including the Digital Personal Data Protection Act, 2023), you have the right to:</p>
    <ul>
      <li>Access and obtain a copy of your personal data.</li>
      <li>Correct inaccurate or incomplete data.</li>
      <li>Delete your account and associated records (subject to lawful retention obligations).</li>
      <li>Withdraw consent to processing (without affecting processing already performed).</li>
      <li>Object to or restrict certain processing.</li>
      <li>Port your data to another service where technically feasible.</li>
      <li>Lodge a complaint with the Data Protection Board of India.</li>
    </ul>
    <p>
      To exercise any of these rights, email <a href="mailto:info@tots360.in">info@tots360.in</a>. We will respond within 30 days.
    </p>

    <h2>10. International transfers</h2>
    <p>
      Our primary infrastructure is hosted in India. Some of our service providers (e.g., Stripe, Google Analytics, Resend) may
      process limited data in other jurisdictions. In such cases, we rely on contractual safeguards to ensure an equivalent level
      of protection.
    </p>

    <h2>11. Cookies</h2>
    <p>
      We use cookies strictly necessary for authentication (session_token) and anonymised first-party analytics. You can disable
      cookies in your browser, but doing so may prevent parts of the Service from functioning correctly.
    </p>

    <h2>12. Changes to this Policy</h2>
    <p>
      We may update this Policy from time to time. Material changes will be communicated by email or an in-app notice. The
      &ldquo;Last updated&rdquo; date at the top of this page will always reflect the latest revision.
    </p>

    <h2>13. Grievance Officer</h2>
    <p>
      In accordance with the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, our
      designated Grievance Officer can be contacted at <a href="mailto:info@tots360.in">info@tots360.in</a>. We will acknowledge
      complaints within 24 hours and endeavour to resolve them within 15 days.
    </p>
  </LegalPageLayout>
);

export default PrivacyPolicyPage;
